import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { WhatsAppContactPopover } from '@/components/WhatsAppContactPopover';
import {
  HardHat,
  ClipboardCheck,
  LayoutDashboard,
  CalendarDays,
  BookOpen,
  GraduationCap,
  FolderKanban,
  BarChart3,
  Users,
  Shield,
  TrendingUp,
  FileText,
  Clock,
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  ChevronDown,
  Zap,
  Target,
  Award,
  Building2,
  Wrench,
  Package,
  ShoppingCart,
  Briefcase,
  FlaskConical,
  Bot,
  KeyRound,
  QrCode,
  MessageCircle,
} from 'lucide-react';

// Intersection Observer hook for scroll animations
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const modules = [
  {
    icon: ClipboardCheck,
    title: 'Checklists e Inspeções',
    desc: 'Crie checklists personalizados (ADF, FVS, FVM, AFM), execute inspeções em campo, registre não conformidades com fotos e gere PDFs com QR Code de verificação pública por terceiros.',
    color: 'from-emerald-500 to-emerald-600',
    bg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
  },
  {
    icon: LayoutDashboard,
    title: 'Dashboard Executivo',
    desc: 'Indicadores em tempo real, gráficos interativos, métricas customizáveis e visão consolidada de qualidade, prazos e custos das obras.',
    color: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    icon: CalendarDays,
    title: 'Cronograma com IA',
    desc: 'Gantt, Kanban, caminho crítico, dependências (FS/SS/FF/SF), linhas de base, EAP, curva S e fluxo de caixa — com interpretação financeira por IA (Gemini).',
    color: 'from-cyan-500 to-cyan-600',
    bg: 'bg-cyan-50',
    iconColor: 'text-cyan-600',
  },
  {
    icon: FlaskConical,
    title: 'Rastreabilidade do Concreto',
    desc: 'Fichas de concretagem vinculadas à FVS, corpos de prova com agenda de rompimento, laudos, fornecedores, traços/fck, laboratórios e busca reversa por NF, placa ou ID do CP.',
    color: 'from-orange-500 to-orange-600',
    bg: 'bg-orange-50',
    iconColor: 'text-orange-600',
  },
  {
    icon: Briefcase,
    title: 'Planejamento e Empreendimentos',
    desc: 'Estruture empreendimentos, vincule obras, defina metas e acompanhe o avanço estratégico do seu portfólio de construção.',
    color: 'from-amber-500 to-amber-600',
    bg: 'bg-amber-50',
    iconColor: 'text-amber-600',
  },
  {
    icon: Package,
    title: 'Controle de Estoque',
    desc: 'Materiais com grupos e subgrupos, saldo por obra, entradas e saídas, transferências entre obras, estoque mínimo e histórico completo de movimentações.',
    color: 'from-yellow-500 to-yellow-600',
    bg: 'bg-yellow-50',
    iconColor: 'text-yellow-600',
  },
  {
    icon: ShoppingCart,
    title: 'Compras e Suprimentos',
    desc: 'Solicitações de compra com filtro por grupo/subgrupo, cotações com fornecedores, ordens de compra, recebimento e integração automática com o estoque.',
    color: 'from-rose-500 to-rose-600',
    bg: 'bg-rose-50',
    iconColor: 'text-rose-600',
  },
  {
    icon: Wrench,
    title: 'Equipamentos',
    desc: 'Cadastro de máquinas e ferramentas, eventos de manutenção, alocação por obra e controle do status operacional de cada equipamento.',
    color: 'from-slate-500 to-slate-600',
    bg: 'bg-slate-50',
    iconColor: 'text-slate-600',
  },
  {
    icon: Users,
    title: 'Mão de Obra',
    desc: 'Funcionários e terceiros, folha de presença digital, horas extras, contratos, períodos de experiência e auditoria de frequência.',
    color: 'from-violet-500 to-violet-600',
    bg: 'bg-violet-50',
    iconColor: 'text-violet-600',
  },
  {
    icon: GraduationCap,
    title: 'Treinamentos',
    desc: 'Matriz de treinamentos, registros obrigatórios e opcionais, vínculo com colaboradores e controle de validade de capacitações.',
    color: 'from-lime-500 to-lime-600',
    bg: 'bg-lime-50',
    iconColor: 'text-lime-600',
  },
  {
    icon: BookOpen,
    title: 'Diário de Obra',
    desc: 'Registro diário de atividades, clima, ocorrências, efetivo, mídias e avanços com calendário visual e exportação consolidada.',
    color: 'from-teal-500 to-teal-600',
    bg: 'bg-teal-50',
    iconColor: 'text-teal-600',
  },
  {
    icon: FolderKanban,
    title: 'Projetos e Documentos',
    desc: 'Lista mestra de documentos, controle de revisões, setores, upload de pranchas e organização centralizada de toda a documentação técnica.',
    color: 'from-indigo-500 to-indigo-600',
    bg: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
  },
  {
    icon: Bot,
    title: 'Chatbots com IA',
    desc: 'Atendimento automatizado via WhatsApp, conversas vinculadas à sua base de obras e respostas treinadas com inteligência artificial.',
    color: 'from-green-500 to-green-600',
    bg: 'bg-green-50',
    iconColor: 'text-green-600',
  },
  {
    icon: KeyRound,
    title: 'Permissões Granulares',
    desc: 'Matriz de permissões por ação e módulo, perfis (Admin, Inspecionador, Consultor) e isolamento multi-tenant com auditoria completa.',
    color: 'from-sky-500 to-sky-600',
    bg: 'bg-sky-50',
    iconColor: 'text-sky-600',
  },
  {
    icon: BarChart3,
    title: 'Relatórios e Auditoria',
    desc: 'Relatórios em PDF, exportação em Excel/CSV, métricas customizáveis, log de auditoria completo e rastreabilidade total das ações.',
    color: 'from-fuchsia-500 to-fuchsia-600',
    bg: 'bg-fuchsia-50',
    iconColor: 'text-fuchsia-600',
  },
];

const benefits = [
  {
    icon: Shield,
    title: 'Qualidade e Conformidade',
    desc: 'Fluxo rigoroso de aprovação, reinspecção, histórico de não conformidades e documentação alinhada às normas técnicas e ao SGQ.',
  },
  {
    icon: QrCode,
    title: 'Rastreabilidade Total',
    desc: 'Do recebimento do concreto ao laudo do CP: busca reversa por nota fiscal, placa do caminhão ou ID e verificação pública por QR Code.',
  },
  {
    icon: Zap,
    title: 'Produtividade Aumentada',
    desc: 'Elimine planilhas e papéis. Tudo digital, acessível de qualquer dispositivo, com dados sincronizados em tempo real.',
  },
  {
    icon: Target,
    title: 'Decisões com Dados e IA',
    desc: 'Dashboards interativos, métricas customizáveis e interpretação financeira por IA para decisões estratégicas mais rápidas.',
  },
];

const stats = [
  { value: '15+', label: 'Módulos integrados' },
  { value: '100%', label: 'Digital e sem papel' },
  { value: 'IA', label: 'Cronograma e WhatsApp' },
  { value: '24/7', label: 'Acesso multiplataforma' },
];

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  
  const modulesSection = useInView();
  const benefitsSection = useInView();
  const statsSection = useInView();
  const ctaSection = useInView();

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Helmet>
        <title>GEROBRA — Gestão Completa de Obras e Qualidade</title>
        <meta name="description" content="Gestão de obras com inspeções digitais, cronograma com IA, rastreabilidade do concreto, estoque, compras, mão de obra e chatbots no WhatsApp." />
        <link rel="canonical" href="https://gerobra.ia.br/" />
        <meta property="og:title" content="GEROBRA — Gestão Completa de Obras e Qualidade" />
        <meta property="og:description" content="Inspeções digitais, cronograma com IA, rastreabilidade do concreto, estoque, compras, mão de obra e chatbots no WhatsApp em uma só plataforma." />
        <meta property="og:url" content="https://gerobra.ia.br/" />
      </Helmet>
      {/* ── Navbar ── */}
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-md border-b border-border'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-16 lg:h-20">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-3 group"
            aria-label="Voltar ao topo da página GEROBRA"
          >
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl shadow-md transition-transform group-hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #8B5A2B 0%, #CD853F 100%)' }}
            >
              <HardHat className="h-5 w-5 text-amber-100" />
            </div>
            <span className={`text-xl font-bold tracking-tight transition-colors ${scrolled ? 'text-foreground' : 'text-white'}`}>
              GEROBRA
            </span>
          </button>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo('modulos')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Módulos
            </button>
            <button onClick={() => scrollTo('beneficios')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Benefícios
            </button>
            <button onClick={() => scrollTo('numeros')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Números
            </button>
            <WhatsAppContactPopover
              trigger={
                <Button
                  variant="outline"
                  className="rounded-full px-5 font-semibold border-2 hover:-translate-y-0.5 transition-all"
                  style={{ borderColor: '#25D366', color: '#128C4E', background: 'white' }}
                >
                  <MessageCircle className="mr-2 h-4 w-4" style={{ color: '#25D366' }} />
                  Solicitar acesso
                </Button>
              }
            />
            <Link to="/login">
              <Button
                className="rounded-full px-6 font-semibold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 text-white"
                style={{ background: 'linear-gradient(135deg, #8B5A2B 0%, #A0522D 100%)' }}
              >
                Entrar
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-2">
            <WhatsAppContactPopover
              trigger={
                <button
                  aria-label="Solicitar acesso via WhatsApp"
                  className="p-2 rounded-full border-2 flex items-center justify-center"
                  style={{ borderColor: '#25D366', background: 'white' }}
                >
                  <MessageCircle className="h-5 w-5" style={{ color: '#25D366' }} />
                </button>
              }
            />
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-lg hover:bg-accent transition-colors"
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-border animate-fade-in">
            <div className="flex flex-col p-6 gap-4">
              <button onClick={() => scrollTo('modulos')} className="text-left text-base font-medium py-2 text-foreground">Módulos</button>
              <button onClick={() => scrollTo('beneficios')} className="text-left text-base font-medium py-2 text-foreground">Benefícios</button>
              <button onClick={() => scrollTo('numeros')} className="text-left text-base font-medium py-2 text-foreground">Números</button>
              <Link to="/login" className="mt-2">
                <Button className="w-full rounded-full font-semibold text-white" style={{ background: 'linear-gradient(135deg, #8B5A2B 0%, #A0522D 100%)' }}>
                  Entrar <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="relative min-h-[90vh] flex items-center pt-24 lg:pt-24">
        {/* Background */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(165deg, #8B5A2B 0%, #A0522D 35%, #CD853F 70%, #DEB887 100%)' }} />
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }} />
        {/* Decorative blobs */}
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-amber-200/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-orange-300/15 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-12 items-center">
          {/* Text */}
          <div className="text-white space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-sm font-medium text-amber-100">
              <Building2 className="h-4 w-4" />
              Plataforma para Construção Civil
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
              Gestão da Qualidade na Construção Civil
              <span className="block mt-2 text-amber-100/90">completa e inteligente</span>
            </h1>
            <p className="text-lg lg:text-xl text-amber-50/95 leading-relaxed max-w-xl">
              Qualidade, cronograma, estoque, compras, equipamentos, mão de obra, diário de obra
              e documentação — tudo em uma única plataforma integrada com inteligência artificial.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/login">
                <Button
                  size="lg"
                  className="rounded-full px-8 h-14 text-base font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 bg-white text-amber-900 hover:bg-amber-50"
                >
                  Acessar plataforma
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                onClick={() => scrollTo('modulos')}
                className="rounded-full px-8 h-14 text-base font-semibold border-2 border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm"
              >
                Ver módulos
                <ChevronDown className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Visual card showcase */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Main card */}
              <div className="bg-white/15 backdrop-blur-xl rounded-3xl border border-white/20 p-8 shadow-2xl">
                <div className="grid grid-cols-2 gap-4">
                  {modules.slice(0, 4).map((m, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 transition-all hover:bg-white/20 hover:scale-[1.02]"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20">
                        <m.icon className="h-5 w-5 text-amber-100" />
                      </div>
                      <span className="text-sm font-medium text-white/90 leading-tight">{m.title}</span>
                    </div>
                  ))}
                </div>
                {/* Mini chart decoration */}
                <div className="mt-6 flex items-end gap-1.5 h-16 px-2">
                  {[40, 65, 50, 80, 60, 90, 75, 95, 70, 85, 92, 88].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t-sm bg-gradient-to-t from-amber-200/40 to-amber-100/60 transition-all"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-white/50">
                  <span>Conformidade por período</span>
                  <span className="text-amber-100 font-semibold">92%</span>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-5 py-3 flex items-center gap-3 border border-amber-100/50 animate-float">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Inspeção aprovada</p>
                  <p className="text-sm font-semibold text-foreground">12 itens conformes</p>
                </div>
              </div>

              {/* Floating badge 2 */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl px-5 py-3 flex items-center gap-3 border border-amber-100/50" style={{ animation: 'float 3s ease-in-out infinite 1.5s' }}>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Conformidade</p>
                  <p className="text-sm font-semibold text-foreground">+95% este mês</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom curve */}
        <div className="absolute bottom-0 inset-x-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80V40C240 0 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="hsl(var(--background))" />
          </svg>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section ref={statsSection.ref} id="numeros" className="py-16 bg-background">
        <div className={`max-w-5xl mx-auto px-6 transition-all duration-700 ${statsSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div
                key={i}
                className="text-center p-6 rounded-2xl bg-card border border-border shadow-sm hover:shadow-md transition-all hover:-translate-y-1"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <p className="text-3xl lg:text-4xl font-bold bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #8B5A2B, #CD853F)' }}>
                  {s.value}
                </p>
                <p className="text-sm text-muted-foreground mt-2 font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Modules ── */}
      <section ref={modulesSection.ref} id="modulos" className="py-20 lg:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${modulesSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent text-accent-foreground text-sm font-medium mb-4">
              <Zap className="h-4 w-4" />
              Funcionalidades
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              Tudo que você precisa em
              <span className="block mt-1" style={{ color: '#8B5A2B' }}> um só lugar</span>
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Módulos integrados que cobrem todas as etapas da gestão de obras, do planejamento à entrega.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {modules.map((m, i) => (
              <div
                key={i}
                className={`group p-6 rounded-2xl bg-card border border-border shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-2 ${modulesSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${m.bg} mb-4 transition-transform group-hover:scale-110`}>
                  <m.icon className={`h-6 w-6 ${m.iconColor}`} />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">{m.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section ref={benefitsSection.ref} id="beneficios" className="py-20 lg:py-28" style={{ background: 'linear-gradient(180deg, hsl(43 35% 96%) 0%, hsl(40 50% 93%) 100%)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${benefitsSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent text-accent-foreground text-sm font-medium mb-4">
              <Award className="h-4 w-4" />
              Vantagens
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              Por que escolher o
              <span className="block mt-1" style={{ color: '#8B5A2B' }}> GEROBRA?</span>
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Simplifique a gestão das suas obras com tecnologia e inteligência.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <div
                key={i}
                className={`relative p-8 rounded-2xl bg-card border border-border shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-2 overflow-hidden group ${benefitsSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Subtle gradient glow on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'radial-gradient(circle at top right, rgba(139,90,43,0.05), transparent 70%)' }} />
                <div className="relative z-10">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl mb-5" style={{ background: 'linear-gradient(135deg, rgba(139,90,43,0.1), rgba(205,133,63,0.15))' }}>
                    <b.icon className="h-7 w-7" style={{ color: '#8B5A2B' }} />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">{b.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it Works ── */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              Simples de usar
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Comece a usar em minutos, sem instalação complexa.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: Users, title: 'Acesse a plataforma', desc: 'Faça login com suas credenciais e acesse de qualquer dispositivo.' },
              { step: '02', icon: ClipboardCheck, title: 'Configure suas obras', desc: 'Cadastre obras, modelos de checklist e organize suas equipes.' },
              { step: '03', icon: TrendingUp, title: 'Acompanhe tudo', desc: 'Monitore indicadores, gere relatórios e tome decisões com dados reais.' },
            ].map((s, i) => (
              <div key={i} className="relative text-center group">
                {/* Connector line */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px bg-border" />
                )}
                <div className="relative inline-flex h-24 w-24 items-center justify-center rounded-3xl bg-card border-2 border-border shadow-sm mb-6 mx-auto group-hover:shadow-md group-hover:border-primary/20 transition-all">
                  <span className="absolute -top-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B5A2B, #CD853F)' }}>
                    {s.step}
                  </span>
                  <s.icon className="h-10 w-10 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section ref={ctaSection.ref} className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #8B5A2B 0%, #A0522D 50%, #6B3A1F 100%)' }} />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
          backgroundSize: '24px 24px',
        }} />
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-300/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-200/10 rounded-full blur-[100px]" />

        <div className={`relative z-10 max-w-3xl mx-auto px-6 text-center transition-all duration-700 ${ctaSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl lg:text-5xl font-bold text-white leading-tight">
            Pronto para transformar a<br />
            gestão das suas obras?
          </h2>
          <p className="text-lg text-amber-100/70 mt-6 max-w-xl mx-auto leading-relaxed">
            Junte-se a empresas que já utilizam o GEROBRA para garantir qualidade, organização e eficiência em seus empreendimentos.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/login">
              <Button
                size="lg"
                className="rounded-full px-10 h-14 text-base font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 bg-white text-amber-900 hover:bg-amber-50"
              >
                Entrar na plataforma
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: 'linear-gradient(135deg, #8B5A2B, #CD853F)' }}>
                <HardHat className="h-4.5 w-4.5 text-amber-100" />
              </div>
              <span className="text-lg font-bold text-foreground">GEROBRA</span>
            </div>

            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/blog/guia-nr-18" className="hover:text-foreground transition-colors">Guia NR 18</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Termos de Uso</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacidade</Link>
              <Link to="/install" className="hover:text-foreground transition-colors">Instalar App</Link>
            </div>

            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} GEROBRA. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
